import { z } from "zod";

const getMasterDataPurchasersSchema = z.object({
  search: z.string().nullable().optional(),
  includeInactive: z.boolean().nullable().optional(),
});

const getMasterDataDistributionCentersSchema = z.object({
  search: z.string().nullable().optional(),
  purchaserId: z.number().int().positive().nullable().optional(),
  page_number: z.number().int().min(1),
  page_size: z.number().int().min(1).max(100),
});

const createMasterDataDistributionCenterSchema = z.object({
  purchaserId: z.number().int().positive(),
  dcName: z.string().trim().min(1).max(255),
  dcCode: z.string().trim().min(1).max(100),
});

const updateMasterDataDistributionCenterSchema = z.object({
  distributionCenterId: z.number().int().positive(),
  dcName: z.string().trim().min(1).max(255),
  dcCode: z.string().trim().min(1).max(100),
});

const getMasterDataProductsSchema = z.object({
  search: z.string().nullable().optional(),
  includeInactive: z.boolean().nullable().optional(),
  page_number: z.number().int().min(1),
  page_size: z.number().int().min(1).max(100),
});

const getMasterDataProductCategoriesSchema = z.object({
  search: z.string().nullable().optional(),
  includeInactive: z.boolean().nullable().optional(),
  page_number: z.number().int().min(1).nullable().optional(),
  page_size: z.number().int().min(1).max(100).nullable().optional(),
});

const getMasterDataProductPricesSchema = z.object({
  search: z.string().nullable().optional(),
  includeInactive: z.boolean().nullable().optional(),
  productId: z.number().int().positive().nullable().optional(),
  page_number: z.number().int().min(1),
  page_size: z.number().int().min(1).max(100),
});

const productPriceAmountSchema = z
  .number()
  .positive()
  .refine(
    (value) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-6,
    "Price must be greater than 0 and have at most 2 decimal places.",
  );

const getMasterDataProductPriceHistorySchema = z.object({
  productId: z.number().int().positive(),
  purchaserId: z.number().int().positive().nullable().optional(),
  period: z.enum(["6", "12", "18"]),
});

const createMasterDataProductPriceSchema = z.object({
  productId: z.number().int().positive(),
  purchaserId: z.number().int().positive().nullable().optional(),
  productPrice: productPriceAmountSchema,
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  notes: z.string().max(500).nullable().optional(),
});

const createMasterDataProductCategorySchema = z.object({
  name: z.string().trim().min(1).max(255),
  categoryKey: z.string().trim().min(1).max(255),
  description: z.string().max(500).nullable().optional(),
  searchVectorKeywords: z.string().max(255).nullable().optional(),
  isActive: z.boolean(),
});

const updateMasterDataProductCategorySchema =
  createMasterDataProductCategorySchema.extend({
    productCategoryId: z.number().int().positive(),
  });

const productUomCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(10)
  .regex(
    /^[A-Z0-9]+(-[A-Z0-9]+)*$/,
    "Only uppercase letters, numbers, and hyphens are allowed",
  );

const getMasterDataProductUomsSchema = z.object({
  search: z.string().nullable().optional(),
});

const createMasterDataProductUomSchema = z.object({
  uomCode: productUomCodeSchema,
  uomName: z.string().trim().min(1).max(100),
  uomDescription: z.string().trim().min(1).max(255),
});

const updateMasterDataProductUomSchema = createMasterDataProductUomSchema.extend({
  uomId: z.number().int().positive(),
});

const deleteMasterDataProductUomsSchema = z.object({
  uomIds: z.array(z.number().int().positive()).min(1),
});

const getMasterDataDeductionCategoriesSchema = z.object({
  search: z.string().nullable().optional(),
  includeInactive: z.boolean().nullable().optional(),
});

const createMasterDataDeductionCategorySchema = z.object({
  categoryName: z.string().trim().min(1).max(255),
  categoryKey: z.string().trim().min(1).max(255),
  isActive: z.boolean(),
});

const updateMasterDataDeductionCategorySchema =
  createMasterDataDeductionCategorySchema.extend({
    categoryId: z.number().int().positive(),
  });

const deleteMasterDataDeductionCategoriesSchema = z.object({
  categoryIds: z.array(z.number().int().positive()).min(1),
});

const getMasterDataDeductionTypesSchema = z.object({
  search: z.string().nullable().optional(),
  child_search: z.string().nullable().optional(),
  page_number: z.number().int().min(1),
  page_size: z.number().int().min(1).max(100),
});

const getMasterDataDeductionSubTypesSchema = getMasterDataDeductionTypesSchema.extend({
  parentId: z.number().int().positive(),
});

const createMasterDataDeductionTypeSchema = z.object({
  name: z.string().trim().min(1).max(255),
  deductionTypeKey: z.string().trim().min(1).max(255),
  deductionTypeCategoryId: z.number().int().positive().nullable().optional(),
  parentId: z.number().int().positive().nullable().optional(),
  helpText: z.string().nullable().optional(),
  metadata: z.unknown().nullable().optional(),
});

const updateMasterDataDeductionTypeSchema = createMasterDataDeductionTypeSchema.extend({
  typeId: z.number().int().positive(),
});

const deleteMasterDataDeductionTypesSchema = z.object({
  typeIds: z.array(z.number().int().positive()).min(1),
});

const deleteAllMasterDataDeductionTypesSchema = z.object({
  parentId: z.number().int().positive().nullable().optional(),
});

const getMasterDataReasonCodeMappingsSchema = z.object({
  search: z.string().nullable().optional(),
  page_number: z.number().int().min(1),
  page_size: z.number().int().min(1).max(100),
});

const createMasterDataReasonCodeMappingSchema = z.object({
  purchaserId: z.number().int().positive(),
  deductionTypeId: z.number().int().positive(),
  reasonCode: z.string().trim().min(1).max(255),
  reasonCodeKey: z.string().trim().min(1).max(255),
  promoTypeId: z.number().int().positive().nullable().optional(),
});

const updateMasterDataReasonCodeMappingSchema =
  createMasterDataReasonCodeMappingSchema.extend({
    mappingId: z.number().int().positive(),
  });

const deleteMasterDataReasonCodeMappingsSchema = z.object({
  mappingIds: z.array(z.number().int().positive()).min(1),
});

const deleteAllMasterDataReasonCodeMappingsSchema = z.object({});

const createMasterDataProductSchema = z.object({
  itemName: z.string().trim().min(1).max(255),
  productKey: z.string().trim().min(1).max(255),
  categoryId: z.number().int().positive(),
  uomId: z.number().int().positive(),
  itemDescription: z.string().trim().min(1).max(500),
  upcItemNumber: z.string().trim().min(1).max(255),
  glItemNumber: z.string().max(255).nullable().optional(),
  glItemDescription: z.string().max(500).nullable().optional(),
  isActive: z.boolean(),
});

const updateMasterDataProductSchema = createMasterDataProductSchema.extend({
  productId: z.number().int().positive(),
});

const optionalJsonSchema = z.unknown().nullable().optional();
const optionalPercentSchema = z.number().min(0).max(9.99).nullable().optional();
const optionalTermDaysSchema = z
  .number()
  .int()
  .min(0)
  .max(999)
  .nullable()
  .optional();

const createMasterDataPurchaserSchema = z.object({
  purchaserName: z.string().trim().min(1).max(255),
  purchaserKey: z.string().trim().min(1).max(50),
  isActive: z.boolean(),
  searchKeywords: z.string().max(255).nullable().optional(),
  defaultProductCategoryId: z.number().int().positive().nullable().optional(),
  metadata: optionalJsonSchema,
  intelligenceFilter: optionalJsonSchema,
  discountPercentageCredit: optionalPercentSchema,
  deductionTypeInvoicePatternForDiscount: optionalJsonSchema,
  discountPercentageDeduction: optionalPercentSchema,
  discountTermAgreementDays: optionalTermDaysSchema,
  additionalFieldMapping: optionalJsonSchema,
  discountTermAgreementDaysTolerance: z.number().int().nullable().optional(),
});

const updateMasterDataPurchaserSchema = createMasterDataPurchaserSchema
  .omit({ purchaserKey: true })
  .extend({
    purchaserId: z.number().int().positive(),
  });

const getMasterDataRetailersSchema = z.object({
  search: z.string().nullable().optional(),
  page_number: z.number().int().min(1),
  page_size: z.number().int().min(1).max(100),
});

const createMasterDataRetailerSchema = z.object({
  retailerName: z.string().trim().min(1),
  retailerKey: z.string().trim().min(1),
});

const updateMasterDataRetailerSchema = createMasterDataRetailerSchema
  .omit({ retailerKey: true })
  .extend({
    retailerId: z.number().int().positive(),
  });

const getMasterDataRetailerMatchRulesSchema = z.object({
  search: z.string().nullable().optional(),
  retailerId: z.number().int().positive().nullable().optional(),
  page_number: z.number().int().min(1),
  page_size: z.number().int().min(1).max(100),
});

const createMasterDataRetailerMatchRuleSchema = z.object({
  retailerId: z.number().int().positive(),
  matchPattern: z.string().trim().min(1),
  matchPriority: z.number().int().min(0),
  sourceColumn: z.string().trim().min(1).max(50),
});

const updateMasterDataRetailerMatchRuleSchema = z.object({
  ruleId: z.number().int().positive(),
  matchPattern: z.string().trim().min(1),
  matchPriority: z.number().int().min(0),
  sourceColumn: z.string().trim().min(1).max(50),
});

const getMasterDataRawRetailerAssociationsSchema = z.object({
  search: z.string().nullable().optional(),
  retailerId: z.number().int().positive().nullable().optional(),
  page_number: z.number().int().min(1),
  page_size: z.number().int().min(1).max(100),
});

const createMasterDataRawRetailerAssociationSchema = z.object({
  retailerId: z.number().int().positive(),
  rawRetailerName: z.string().trim().min(1),
});

const updateMasterDataRawRetailerAssociationSchema = z.object({
  rawRetailerId: z.number().int().positive(),
  rawRetailerName: z.string().trim().min(1),
});

export type GetMasterDataPurchasersDTOShape = z.infer<
  typeof getMasterDataPurchasersSchema
>;
export type GetMasterDataDistributionCentersDTOShape = z.infer<
  typeof getMasterDataDistributionCentersSchema
>;
export type CreateMasterDataDistributionCenterDTOShape = z.infer<
  typeof createMasterDataDistributionCenterSchema
>;
export type UpdateMasterDataDistributionCenterDTOShape = z.infer<
  typeof updateMasterDataDistributionCenterSchema
>;
export type GetMasterDataProductsDTOShape = z.infer<
  typeof getMasterDataProductsSchema
>;
export type GetMasterDataProductCategoriesDTOShape = z.infer<
  typeof getMasterDataProductCategoriesSchema
>;
export type GetMasterDataProductPricesDTOShape = z.infer<
  typeof getMasterDataProductPricesSchema
>;
export type GetMasterDataProductPriceHistoryDTOShape = z.infer<
  typeof getMasterDataProductPriceHistorySchema
>;
export type CreateMasterDataProductPriceDTOShape = z.infer<
  typeof createMasterDataProductPriceSchema
>;
export type CreateMasterDataProductCategoryDTOShape = z.infer<
  typeof createMasterDataProductCategorySchema
>;
export type UpdateMasterDataProductCategoryDTOShape = z.infer<
  typeof updateMasterDataProductCategorySchema
>;
export type GetMasterDataProductUomsDTOShape = z.infer<
  typeof getMasterDataProductUomsSchema
>;
export type CreateMasterDataProductUomDTOShape = z.infer<
  typeof createMasterDataProductUomSchema
>;
export type UpdateMasterDataProductUomDTOShape = z.infer<
  typeof updateMasterDataProductUomSchema
>;
export type DeleteMasterDataProductUomsDTOShape = z.infer<
  typeof deleteMasterDataProductUomsSchema
>;
export type GetMasterDataDeductionCategoriesDTOShape = z.infer<
  typeof getMasterDataDeductionCategoriesSchema
>;
export type CreateMasterDataDeductionCategoryDTOShape = z.infer<
  typeof createMasterDataDeductionCategorySchema
>;
export type UpdateMasterDataDeductionCategoryDTOShape = z.infer<
  typeof updateMasterDataDeductionCategorySchema
>;
export type DeleteMasterDataDeductionCategoriesDTOShape = z.infer<
  typeof deleteMasterDataDeductionCategoriesSchema
>;
export type GetMasterDataDeductionTypesDTOShape = z.infer<
  typeof getMasterDataDeductionTypesSchema
>;
export type GetMasterDataDeductionSubTypesDTOShape = z.infer<
  typeof getMasterDataDeductionSubTypesSchema
>;
export type CreateMasterDataDeductionTypeDTOShape = z.infer<
  typeof createMasterDataDeductionTypeSchema
>;
export type UpdateMasterDataDeductionTypeDTOShape = z.infer<
  typeof updateMasterDataDeductionTypeSchema
>;
export type DeleteMasterDataDeductionTypesDTOShape = z.infer<
  typeof deleteMasterDataDeductionTypesSchema
>;
export type DeleteAllMasterDataDeductionTypesDTOShape = z.infer<
  typeof deleteAllMasterDataDeductionTypesSchema
>;
export type GetMasterDataReasonCodeMappingsDTOShape = z.infer<
  typeof getMasterDataReasonCodeMappingsSchema
>;
export type CreateMasterDataReasonCodeMappingDTOShape = z.infer<
  typeof createMasterDataReasonCodeMappingSchema
>;
export type UpdateMasterDataReasonCodeMappingDTOShape = z.infer<
  typeof updateMasterDataReasonCodeMappingSchema
>;
export type DeleteMasterDataReasonCodeMappingsDTOShape = z.infer<
  typeof deleteMasterDataReasonCodeMappingsSchema
>;
export type DeleteAllMasterDataReasonCodeMappingsDTOShape = z.infer<
  typeof deleteAllMasterDataReasonCodeMappingsSchema
>;
export type CreateMasterDataProductDTOShape = z.infer<
  typeof createMasterDataProductSchema
>;
export type UpdateMasterDataProductDTOShape = z.infer<
  typeof updateMasterDataProductSchema
>;
export type CreateMasterDataPurchaserDTOShape = z.infer<
  typeof createMasterDataPurchaserSchema
>;
export type UpdateMasterDataPurchaserDTOShape = z.infer<
  typeof updateMasterDataPurchaserSchema
>;
export type GetMasterDataRetailersDTOShape = z.infer<
  typeof getMasterDataRetailersSchema
>;
export type CreateMasterDataRetailerDTOShape = z.infer<
  typeof createMasterDataRetailerSchema
>;
export type UpdateMasterDataRetailerDTOShape = z.infer<
  typeof updateMasterDataRetailerSchema
>;
export type GetMasterDataRetailerMatchRulesDTOShape = z.infer<
  typeof getMasterDataRetailerMatchRulesSchema
>;
export type CreateMasterDataRetailerMatchRuleDTOShape = z.infer<
  typeof createMasterDataRetailerMatchRuleSchema
>;
export type UpdateMasterDataRetailerMatchRuleDTOShape = z.infer<
  typeof updateMasterDataRetailerMatchRuleSchema
>;
export type GetMasterDataRawRetailerAssociationsDTOShape = z.infer<
  typeof getMasterDataRawRetailerAssociationsSchema
>;
export type CreateMasterDataRawRetailerAssociationDTOShape = z.infer<
  typeof createMasterDataRawRetailerAssociationSchema
>;
export type UpdateMasterDataRawRetailerAssociationDTOShape = z.infer<
  typeof updateMasterDataRawRetailerAssociationSchema
>;

export class GetMasterDataPurchasersDTO {
  readonly search?: string | null;
  readonly includeInactive?: boolean | null;

  constructor(data: GetMasterDataPurchasersDTOShape) {
    const validated = getMasterDataPurchasersSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataPurchasersSchema.safeParse(data);
  }
}

export class GetMasterDataDistributionCentersDTO {
  readonly search?: string | null;
  readonly purchaserId?: number | null;
  readonly page_number!: number;
  readonly page_size!: number;

  constructor(data: GetMasterDataDistributionCentersDTOShape) {
    const validated = getMasterDataDistributionCentersSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataDistributionCentersSchema.safeParse(data);
  }
}

export class CreateMasterDataDistributionCenterDTO {
  readonly purchaserId!: number;
  readonly dcName!: string;
  readonly dcCode!: string;

  constructor(data: CreateMasterDataDistributionCenterDTOShape) {
    const validated = createMasterDataDistributionCenterSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return createMasterDataDistributionCenterSchema.safeParse(data);
  }
}

export class UpdateMasterDataDistributionCenterDTO {
  readonly distributionCenterId!: number;
  readonly dcName!: string;
  readonly dcCode!: string;

  constructor(data: UpdateMasterDataDistributionCenterDTOShape) {
    const validated = updateMasterDataDistributionCenterSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return updateMasterDataDistributionCenterSchema.safeParse(data);
  }
}

export class GetMasterDataProductsDTO {
  readonly search?: string | null;
  readonly includeInactive?: boolean | null;
  readonly page_number!: number;
  readonly page_size!: number;

  constructor(data: GetMasterDataProductsDTOShape) {
    const validated = getMasterDataProductsSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataProductsSchema.safeParse(data);
  }
}

export class GetMasterDataProductCategoriesDTO {
  readonly search?: string | null;
  readonly includeInactive?: boolean | null;
  readonly page_number?: number | null;
  readonly page_size?: number | null;

  constructor(data: GetMasterDataProductCategoriesDTOShape) {
    const validated = getMasterDataProductCategoriesSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataProductCategoriesSchema.safeParse(data);
  }
}

export class GetMasterDataProductPricesDTO {
  readonly search?: string | null;
  readonly includeInactive?: boolean | null;
  readonly productId?: number | null;
  readonly page_number!: number;
  readonly page_size!: number;

  constructor(data: GetMasterDataProductPricesDTOShape) {
    const validated = getMasterDataProductPricesSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataProductPricesSchema.safeParse(data);
  }
}

export class GetMasterDataProductPriceHistoryDTO {
  readonly productId!: number;
  readonly purchaserId?: number | null;
  readonly period!: "6" | "12" | "18";

  constructor(data: GetMasterDataProductPriceHistoryDTOShape) {
    const validated = getMasterDataProductPriceHistorySchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataProductPriceHistorySchema.safeParse(data);
  }
}

export class CreateMasterDataProductPriceDTO {
  readonly productId!: number;
  readonly purchaserId?: number | null;
  readonly productPrice!: number;
  readonly startDate!: string;
  readonly notes?: string | null;

  constructor(data: CreateMasterDataProductPriceDTOShape) {
    const validated = createMasterDataProductPriceSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return createMasterDataProductPriceSchema.safeParse(data);
  }
}

export class CreateMasterDataProductCategoryDTO {
  readonly name!: string;
  readonly categoryKey!: string;
  readonly description?: string | null;
  readonly searchVectorKeywords?: string | null;
  readonly isActive!: boolean;

  constructor(data: CreateMasterDataProductCategoryDTOShape) {
    const validated = createMasterDataProductCategorySchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return createMasterDataProductCategorySchema.safeParse(data);
  }
}

export class UpdateMasterDataProductCategoryDTO {
  readonly productCategoryId!: number;
  readonly name!: string;
  readonly categoryKey!: string;
  readonly description?: string | null;
  readonly searchVectorKeywords?: string | null;
  readonly isActive!: boolean;

  constructor(data: UpdateMasterDataProductCategoryDTOShape) {
    const validated = updateMasterDataProductCategorySchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return updateMasterDataProductCategorySchema.safeParse(data);
  }
}

export class GetMasterDataProductUomsDTO {
  readonly search?: string | null;

  constructor(data: GetMasterDataProductUomsDTOShape) {
    const validated = getMasterDataProductUomsSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataProductUomsSchema.safeParse(data);
  }
}

export class CreateMasterDataProductUomDTO {
  readonly uomCode!: string;
  readonly uomName!: string;
  readonly uomDescription!: string;

  constructor(data: CreateMasterDataProductUomDTOShape) {
    const validated = createMasterDataProductUomSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return createMasterDataProductUomSchema.safeParse(data);
  }
}

export class UpdateMasterDataProductUomDTO {
  readonly uomId!: number;
  readonly uomCode!: string;
  readonly uomName!: string;
  readonly uomDescription!: string;

  constructor(data: UpdateMasterDataProductUomDTOShape) {
    const validated = updateMasterDataProductUomSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return updateMasterDataProductUomSchema.safeParse(data);
  }
}

export class DeleteMasterDataProductUomsDTO {
  readonly uomIds!: number[];

  constructor(data: DeleteMasterDataProductUomsDTOShape) {
    const validated = deleteMasterDataProductUomsSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return deleteMasterDataProductUomsSchema.safeParse(data);
  }
}

export class CreateMasterDataProductDTO {
  readonly itemName!: string;
  readonly productKey!: string;
  readonly categoryId!: number;
  readonly uomId!: number;
  readonly itemDescription!: string;
  readonly upcItemNumber!: string;
  readonly glItemNumber?: string | null;
  readonly glItemDescription?: string | null;
  readonly isActive!: boolean;

  constructor(data: CreateMasterDataProductDTOShape) {
    const validated = createMasterDataProductSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return createMasterDataProductSchema.safeParse(data);
  }
}

export class UpdateMasterDataProductDTO {
  readonly productId!: number;
  readonly itemName!: string;
  readonly productKey!: string;
  readonly categoryId!: number;
  readonly uomId!: number;
  readonly itemDescription!: string;
  readonly upcItemNumber!: string;
  readonly glItemNumber?: string | null;
  readonly glItemDescription?: string | null;
  readonly isActive!: boolean;

  constructor(data: UpdateMasterDataProductDTOShape) {
    const validated = updateMasterDataProductSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return updateMasterDataProductSchema.safeParse(data);
  }
}

export class CreateMasterDataPurchaserDTO {
  readonly purchaserName!: string;
  readonly purchaserKey!: string;
  readonly isActive!: boolean;
  readonly searchKeywords?: string | null;
  readonly defaultProductCategoryId?: number | null;
  readonly metadata?: unknown;
  readonly intelligenceFilter?: unknown;
  readonly discountPercentageCredit?: number | null;
  readonly deductionTypeInvoicePatternForDiscount?: unknown;
  readonly discountPercentageDeduction?: number | null;
  readonly discountTermAgreementDays?: number | null;
  readonly additionalFieldMapping?: unknown;
  readonly discountTermAgreementDaysTolerance?: number | null;

  constructor(data: CreateMasterDataPurchaserDTOShape) {
    const validated = createMasterDataPurchaserSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return createMasterDataPurchaserSchema.safeParse(data);
  }
}

export class UpdateMasterDataPurchaserDTO {
  readonly purchaserId!: number;
  readonly purchaserName!: string;
  readonly isActive!: boolean;
  readonly searchKeywords?: string | null;
  readonly defaultProductCategoryId?: number | null;
  readonly metadata?: unknown;
  readonly intelligenceFilter?: unknown;
  readonly discountPercentageCredit?: number | null;
  readonly deductionTypeInvoicePatternForDiscount?: unknown;
  readonly discountPercentageDeduction?: number | null;
  readonly discountTermAgreementDays?: number | null;
  readonly additionalFieldMapping?: unknown;
  readonly discountTermAgreementDaysTolerance?: number | null;

  constructor(data: UpdateMasterDataPurchaserDTOShape) {
    const validated = updateMasterDataPurchaserSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return updateMasterDataPurchaserSchema.safeParse(data);
  }
}

export class GetMasterDataRetailersDTO {
  readonly search?: string | null;
  readonly page_number!: number;
  readonly page_size!: number;

  constructor(data: GetMasterDataRetailersDTOShape) {
    const validated = getMasterDataRetailersSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataRetailersSchema.safeParse(data);
  }
}

export class CreateMasterDataRetailerDTO {
  readonly retailerName!: string;
  readonly retailerKey!: string;

  constructor(data: CreateMasterDataRetailerDTOShape) {
    const validated = createMasterDataRetailerSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return createMasterDataRetailerSchema.safeParse(data);
  }
}

export class UpdateMasterDataRetailerDTO {
  readonly retailerId!: number;
  readonly retailerName!: string;

  constructor(data: UpdateMasterDataRetailerDTOShape) {
    const validated = updateMasterDataRetailerSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return updateMasterDataRetailerSchema.safeParse(data);
  }
}

export class GetMasterDataRetailerMatchRulesDTO {
  readonly search?: string | null;
  readonly retailerId?: number | null;
  readonly page_number!: number;
  readonly page_size!: number;

  constructor(data: GetMasterDataRetailerMatchRulesDTOShape) {
    const validated = getMasterDataRetailerMatchRulesSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataRetailerMatchRulesSchema.safeParse(data);
  }
}

export class CreateMasterDataRetailerMatchRuleDTO {
  readonly retailerId!: number;
  readonly matchPattern!: string;
  readonly matchPriority!: number;
  readonly sourceColumn!: string;

  constructor(data: CreateMasterDataRetailerMatchRuleDTOShape) {
    const validated = createMasterDataRetailerMatchRuleSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return createMasterDataRetailerMatchRuleSchema.safeParse(data);
  }
}

export class UpdateMasterDataRetailerMatchRuleDTO {
  readonly ruleId!: number;
  readonly matchPattern!: string;
  readonly matchPriority!: number;
  readonly sourceColumn!: string;

  constructor(data: UpdateMasterDataRetailerMatchRuleDTOShape) {
    const validated = updateMasterDataRetailerMatchRuleSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return updateMasterDataRetailerMatchRuleSchema.safeParse(data);
  }
}

export class GetMasterDataRawRetailerAssociationsDTO {
  readonly search?: string | null;
  readonly retailerId?: number | null;
  readonly page_number!: number;
  readonly page_size!: number;

  constructor(data: GetMasterDataRawRetailerAssociationsDTOShape) {
    const validated = getMasterDataRawRetailerAssociationsSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataRawRetailerAssociationsSchema.safeParse(data);
  }
}

export class CreateMasterDataRawRetailerAssociationDTO {
  readonly retailerId!: number;
  readonly rawRetailerName!: string;

  constructor(data: CreateMasterDataRawRetailerAssociationDTOShape) {
    const validated = createMasterDataRawRetailerAssociationSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return createMasterDataRawRetailerAssociationSchema.safeParse(data);
  }
}

export class UpdateMasterDataRawRetailerAssociationDTO {
  readonly rawRetailerId!: number;
  readonly rawRetailerName!: string;

  constructor(data: UpdateMasterDataRawRetailerAssociationDTOShape) {
    const validated = updateMasterDataRawRetailerAssociationSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return updateMasterDataRawRetailerAssociationSchema.safeParse(data);
  }
}

export class GetMasterDataDeductionCategoriesDTO {
  readonly search?: string | null;
  readonly includeInactive?: boolean | null;

  constructor(data: GetMasterDataDeductionCategoriesDTOShape) {
    const validated = getMasterDataDeductionCategoriesSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataDeductionCategoriesSchema.safeParse(data);
  }
}

export class CreateMasterDataDeductionCategoryDTO {
  readonly categoryName!: string;
  readonly categoryKey!: string;
  readonly isActive!: boolean;

  constructor(data: CreateMasterDataDeductionCategoryDTOShape) {
    const validated = createMasterDataDeductionCategorySchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return createMasterDataDeductionCategorySchema.safeParse(data);
  }
}

export class UpdateMasterDataDeductionCategoryDTO {
  readonly categoryId!: number;
  readonly categoryName!: string;
  readonly categoryKey!: string;
  readonly isActive!: boolean;

  constructor(data: UpdateMasterDataDeductionCategoryDTOShape) {
    const validated = updateMasterDataDeductionCategorySchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return updateMasterDataDeductionCategorySchema.safeParse(data);
  }
}

export class DeleteMasterDataDeductionCategoriesDTO {
  readonly categoryIds!: number[];

  constructor(data: DeleteMasterDataDeductionCategoriesDTOShape) {
    const validated = deleteMasterDataDeductionCategoriesSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return deleteMasterDataDeductionCategoriesSchema.safeParse(data);
  }
}

export class GetMasterDataDeductionTypesDTO {
  readonly search?: string | null;
  readonly child_search?: string | null;
  readonly page_number!: number;
  readonly page_size!: number;

  constructor(data: GetMasterDataDeductionTypesDTOShape) {
    const validated = getMasterDataDeductionTypesSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataDeductionTypesSchema.safeParse(data);
  }
}

export class GetMasterDataDeductionSubTypesDTO {
  readonly parentId!: number;
  readonly search?: string | null;
  readonly page_number!: number;
  readonly page_size!: number;

  constructor(data: GetMasterDataDeductionSubTypesDTOShape) {
    const validated = getMasterDataDeductionSubTypesSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataDeductionSubTypesSchema.safeParse(data);
  }
}

export class CreateMasterDataDeductionTypeDTO {
  readonly name!: string;
  readonly deductionTypeKey!: string;
  readonly deductionTypeCategoryId?: number | null;
  readonly parentId?: number | null;
  readonly helpText?: string | null;
  readonly metadata?: unknown;

  constructor(data: CreateMasterDataDeductionTypeDTOShape) {
    const validated = createMasterDataDeductionTypeSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return createMasterDataDeductionTypeSchema.safeParse(data);
  }
}

export class UpdateMasterDataDeductionTypeDTO {
  readonly typeId!: number;
  readonly name!: string;
  readonly deductionTypeKey!: string;
  readonly deductionTypeCategoryId?: number | null;
  readonly parentId?: number | null;
  readonly helpText?: string | null;
  readonly metadata?: unknown;

  constructor(data: UpdateMasterDataDeductionTypeDTOShape) {
    const validated = updateMasterDataDeductionTypeSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return updateMasterDataDeductionTypeSchema.safeParse(data);
  }
}

export class DeleteMasterDataDeductionTypesDTO {
  readonly typeIds!: number[];

  constructor(data: DeleteMasterDataDeductionTypesDTOShape) {
    const validated = deleteMasterDataDeductionTypesSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return deleteMasterDataDeductionTypesSchema.safeParse(data);
  }
}

export class DeleteAllMasterDataDeductionTypesDTO {
  readonly parentId?: number | null;

  constructor(data: DeleteAllMasterDataDeductionTypesDTOShape = {}) {
    const validated = deleteAllMasterDataDeductionTypesSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return deleteAllMasterDataDeductionTypesSchema.safeParse(data);
  }
}

export class GetMasterDataReasonCodeMappingsDTO {
  readonly search?: string | null;
  readonly page_number!: number;
  readonly page_size!: number;

  constructor(data: GetMasterDataReasonCodeMappingsDTOShape) {
    const validated = getMasterDataReasonCodeMappingsSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return getMasterDataReasonCodeMappingsSchema.safeParse(data);
  }
}

export class CreateMasterDataReasonCodeMappingDTO {
  readonly purchaserId!: number;
  readonly deductionTypeId!: number;
  readonly reasonCode!: string;
  readonly reasonCodeKey!: string;
  readonly promoTypeId?: number | null;

  constructor(data: CreateMasterDataReasonCodeMappingDTOShape) {
    const validated = createMasterDataReasonCodeMappingSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return createMasterDataReasonCodeMappingSchema.safeParse(data);
  }
}

export class UpdateMasterDataReasonCodeMappingDTO {
  readonly mappingId!: number;
  readonly purchaserId!: number;
  readonly deductionTypeId!: number;
  readonly reasonCode!: string;
  readonly reasonCodeKey!: string;
  readonly promoTypeId?: number | null;

  constructor(data: UpdateMasterDataReasonCodeMappingDTOShape) {
    const validated = updateMasterDataReasonCodeMappingSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return updateMasterDataReasonCodeMappingSchema.safeParse(data);
  }
}

export class DeleteMasterDataReasonCodeMappingsDTO {
  readonly mappingIds!: number[];

  constructor(data: DeleteMasterDataReasonCodeMappingsDTOShape) {
    const validated = deleteMasterDataReasonCodeMappingsSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return deleteMasterDataReasonCodeMappingsSchema.safeParse(data);
  }
}

export class DeleteAllMasterDataReasonCodeMappingsDTO {
  constructor(data: DeleteAllMasterDataReasonCodeMappingsDTOShape = {}) {
    deleteAllMasterDataReasonCodeMappingsSchema.parse(data);
  }

  static validate(data: unknown) {
    return deleteAllMasterDataReasonCodeMappingsSchema.safeParse(data);
  }
}
